import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 * @property {HTMLDivElement} [backdrop] - The dimmed backdrop behind the panel.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Element[]} Page regions made inert while the root panel is open */
  #inertRegions = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    // The nearest OPEN details: with focus on a closed submenu's summary, Escape
    // used to "close" that submenu and leave the drawer open (WS20 Lane E).
    return event.target.closest('details[open]') ?? this.refs.details;
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§1 */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§2 */
  #showOverlay() {
    for (const element of [this.refs.backdrop, this.refs.menuDrawer]) {
      if (!(element instanceof HTMLElement) || typeof element.showPopover !== 'function') continue;
      if (!element.matches(':popover-open')) element.showPopover();
    }
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§3 */
  #hideOverlay() {
    for (const element of [this.refs.menuDrawer, this.refs.backdrop]) {
      if (!(element instanceof HTMLElement) || typeof element.hidePopover !== 'function') continue;
      if (element.matches(':popover-open')) element.hidePopover();
    }
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // Before the class flip, so the panel is already in the top layer — and already
    // rendered — when the entrance transition starts on the next frame.
    this.#showOverlay();
    if (details === this.refs.details) this.#setPageInert(true);

    const releaseAccordionAnimations = this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // ↗ .cursor/references/code-notes/header-drawer.js.md#§4
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          releaseAccordionAnimations();
          trapFocus(details);
          this.#focusInto(details);
        },
        { subtree: false }
      );
    });
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§7 */
  #focusInto(details) {
    const target =
      details === this.refs.details
        ? this.refs.menuDrawer.querySelector('.menu-drawer__close-button')
        : details.querySelector('.menu-drawer__back-button');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§8 */
  #setPageInert(on) {
    if (on) {
      this.#inertRegions = [
        ...document.querySelectorAll(
          '#MainContent, body > footer, .shopify-section-group-header-group:not(.header-section), .r-header-announcements'
        ),
      ].filter((region) => !region.contains(this) && !region.hasAttribute('inert'));
      for (const region of this.#inertRegions) region.setAttribute('inert', '');
      return;
    }
    for (const region of this.#inertRegions) region.removeAttribute('inert');
    this.#inertRegions = [];
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§5 */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          this.#hideOverlay();
          removeTrapFocus();
          this.#setPageInert(false);
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
        // Focus goes back to the control that opened this panel — the hamburger for
        // the root drawer, the submenu's own summary for a pushed panel.
        summary.focus({ preventScroll: true });
      },
      { subtree: false }
    );
  }

  /* ↗ .cursor/references/code-notes/header-drawer.js.md#§6 */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements and returns the callback that
   * removes it — call it once the drawer's own open animation has finished.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   * @returns {() => void} Re-enables accordion animations
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    return () => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    };
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
